import { useEffect, useState } from 'react'
import { AlertTriangle, X } from 'lucide-react'

/**
 * Cutover notice for the DealHub → PreSales Hub move.
 *
 * Two jobs:
 *  - a persistent banner at the top of every page while the lock is on
 *  - a transient alert when someone actually tries to edit something
 *
 * The lock state comes from the server (GET /api/readonly), so switching the
 * DEALHUB_READONLY var flips the UI too — the message is never hardcoded in
 * two places.
 *
 * The alert fires off the `dealhub:readonly` window event, which api/client.ts
 * dispatches whenever a write comes back 423. That means every write path is
 * covered without touching individual forms or buttons.
 */
export function ReadOnlyBanner() {
  const [message, setMessage] = useState<string | null>(null)
  const [blockedAt, setBlockedAt] = useState<number | null>(null)
  const [dismissedAlert, setDismissedAlert] = useState(false)

  useEffect(() => {
    let cancelled = false
    fetch('/api/readonly')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!cancelled && d?.readOnly && d?.message) setMessage(d.message)
      })
      .catch(() => { /* if this fails the server lock still holds; stay quiet */ })
    return () => { cancelled = true }
  }, [])

  // A blocked write. Surface the message even if the banner fetch failed.
  useEffect(() => {
    const onBlocked = (e: Event) => {
      const detail = (e as CustomEvent<{ message?: string }>).detail
      if (detail?.message) setMessage((prev) => prev ?? detail.message!)
      setDismissedAlert(false)
      setBlockedAt(Date.now())
    }
    window.addEventListener('dealhub:readonly', onBlocked)
    return () => window.removeEventListener('dealhub:readonly', onBlocked)
  }, [])

  if (!message) return null

  const showAlert = blockedAt !== null && !dismissedAlert

  return (
    <>
      {/* Persistent banner */}
      <div
        role="status"
        className="flex items-start gap-3 rounded-lg border border-amber-400/50 bg-amber-50 px-4 py-3 mb-4 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-200"
      >
        <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" aria-hidden="true" />
        <p className="leading-relaxed">{message}</p>
      </div>

      {/* Transient alert — only after an edit is actually attempted */}
      {showAlert && (
        <div
          role="alert"
          aria-live="assertive"
          className="fixed inset-x-0 top-4 z-[100] mx-auto flex max-w-xl items-start gap-3 rounded-lg border border-amber-500 bg-amber-100 px-4 py-3 shadow-lg text-sm text-amber-900 dark:border-amber-500 dark:bg-amber-900 dark:text-amber-100"
        >
          <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" aria-hidden="true" />
          <div className="flex-1">
            <p className="font-semibold mb-1">That change was not saved</p>
            <p className="leading-relaxed">{message}</p>
          </div>
          <button
            type="button"
            onClick={() => setDismissedAlert(true)}
            aria-label="Dismiss"
            className="shrink-0 rounded p-1 hover:bg-amber-200/60 dark:hover:bg-amber-800"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      )}
    </>
  )
}
